import {
  Odometer,
  Promotion,
  Aim,
  Picture,
  UserFilled,
  Wallet,
  Memo,
  Connection,
  TrendCharts,
  Link,
  Bell,
  Monitor,
  Loading,
  MagicStick,
  Notebook,
  Search,
  Goods,
  Shop,
  Money,
  Histogram,
  Collection,
  DataAnalysis,
  Setting,
} from '@element-plus/icons-vue';
import type { RouteLocationRaw } from 'vue-router';

export interface NavItem {
  name: string;
  label: string;
  icon: typeof Odometer;
  to?: RouteLocationRaw;
  featureKey?: string;
}
export interface NavSection {
  /** Section title shown above items; null = no header (used for solo Dashboard) */
  title?: string;
  /** Optional clickable entry for the section title itself. */
  entry?: NavItem;
  /** Stable key used to remember which section is open. Sections without a key
   *  (Dashboard, Settings) are always visible — they're solo pinned items. */
  key?: string;
  featureKey?: string;
  items: NavItem[];
}

export function filterNavSectionsByFeatures(
  sections: NavSection[],
  hasFeature: (featureKey?: string) => boolean,
): NavSection[] {
  return sections
    .map((section) => {
      const entry =
        section.entry && hasFeature(section.entry.featureKey)
          ? section.entry
          : undefined;
      const items = section.items.filter((item) => hasFeature(item.featureKey));
      return { ...section, entry, items };
    })
    .filter((section) => section.items.length > 0 || !!section.entry);
}

// Single source of truth for the authenticated app's navigation. Consumed by
// both the sidebar (MainLayout) and the command palette so they never drift.
export const navSections: NavSection[] = [
  {
    // No key → always visible (pinned, no header).
    items: [{ name: 'dashboard', label: 'nav.dashboard', icon: Odometer, featureKey: 'dashboard' }],
  },
  {
    key: 'manage',
    title: 'navSection.delivery',
    featureKey: 'delivery',
    items: [
      { name: 'ad-management', label: 'nav.ad_management', icon: Promotion, featureKey: 'delivery.ad_management' },
      { name: 'batch-create', label: 'nav.batch_create', icon: MagicStick, featureKey: 'delivery.batch_create' },
      { name: 'one-click-launch', label: 'nav.one_click_launch', icon: MagicStick, featureKey: 'delivery.one_click_launch' },
      { name: 'one-click-records', label: 'nav.one_click_records', icon: Notebook, to: { name: 'one-click-launch', query: { view: 'records' } }, featureKey: 'delivery.one_click_launch' },
      { name: 'creative-lifecycle', label: 'nav.creative_lifecycle', icon: TrendCharts, featureKey: 'delivery.creative_lifecycle' },
      { name: 'audience-lifecycle', label: 'nav.audience_lifecycle', icon: UserFilled, featureKey: 'delivery.audience_lifecycle' },
    ],
  },
  {
    key: 'autopilot',
    title: 'navSection.autopilot',
    featureKey: 'autopilot',
    items: [
      { name: 'autopilot', label: 'nav.autopilot', icon: Promotion, featureKey: 'autopilot.overview' },
      { name: 'autopilot-policies', label: 'nav.autopilot_policies', icon: Setting, featureKey: 'autopilot.policies' },
      { name: 'autopilot-actions', label: 'nav.autopilot_actions', icon: Loading, featureKey: 'autopilot.actions' },
    ],
  },
  {
    key: 'smart',
    title: 'navSection.optimize',
    featureKey: 'optimize',
    items: [
      { name: 'diagnostics', label: 'nav.diagnostics', icon: MagicStick, featureKey: 'optimize.diagnostics' },
      { name: 'test-planner', label: 'nav.test_planner', icon: MagicStick, featureKey: 'optimize.test_planner' },
      { name: 'insights-library', label: 'nav.insights_library', icon: Notebook, featureKey: 'optimize.insights_library' },
      { name: 'experiments', label: 'nav.experiments', icon: Aim, featureKey: 'optimize.experiments' },
      { name: 'automation-rules', label: 'nav.automation_rules', icon: Setting, featureKey: 'optimize.automation_rules' },
      { name: 'ai-assistant', label: 'nav.ai_assistant', icon: MagicStick, featureKey: 'optimize.ai_assistant' },
    ],
  },
  {
    // 报表 — all reporting in one place (was split between 智能优化 and 报表与监控).
    key: 'reports',
    title: 'navSection.reports',
    featureKey: 'reports',
    items: [
      { name: 'report-center', label: 'nav.report_center', icon: Collection, featureKey: 'reports.center' },
      { name: 'profit', label: 'nav.profit', icon: Money, featureKey: 'reports.profit' },
      { name: 'reports', label: 'nav.reports', icon: TrendCharts, featureKey: 'reports.basic' },
      { name: 'pivot-report', label: 'nav.pivot_report', icon: DataAnalysis, featureKey: 'reports.pivot' },
      { name: 'profit-drilldown', label: 'nav.profit_drilldown', icon: Histogram, featureKey: 'reports.profit_drilldown' },
      { name: 'profit-breakdown', label: 'nav.profit_breakdown', icon: Histogram, featureKey: 'reports.profit_breakdown' },
      {
        name: 'product-report',
        label: 'nav.product_report',
        icon: Goods,
        featureKey: 'reports.product',
        to: { name: 'profit', query: { preset: '30d', anchor: 'byProduct' } },
      },
      { name: 'store-report', label: 'nav.store_report', icon: Shop, featureKey: 'reports.store' },
      {
        name: 'creative-board',
        label: 'nav.creative_board',
        icon: Picture,
        featureKey: 'reports.creative_board',
        to: { name: 'creatives' },
      },
      {
        name: 'meta-objective-report',
        label: 'nav.meta_objective_report',
        icon: DataAnalysis,
        featureKey: 'reports.meta_objective',
        to: { name: 'profit-drilldown', query: { report: 'meta_objective' } },
      },
      { name: 'custom-report', label: 'nav.custom_report', icon: DataAnalysis, featureKey: 'reports.custom' },
    ],
  },
  {
    // 监控告警 — operational watch + alerts (own delivery, not competitor intel).
    key: 'monitor',
    title: 'navSection.monitor',
    featureKey: 'monitor',
    items: [
      { name: 'notifications', label: 'nav.notifications', icon: Bell, featureKey: 'monitor.notifications' },
      { name: 'tasks', label: 'nav.tasks', icon: Loading, featureKey: 'monitor.tasks' },
      { name: 'data-quality', label: 'nav.data_quality', icon: Connection, featureKey: 'monitor.data_quality' },
      { name: 'domain-monitoring', label: 'nav.domain_monitoring', icon: Monitor, featureKey: 'monitor.domain' },
    ],
  },
  {
    key: 'intel',
    title: 'navSection.intel',
    featureKey: 'intel',
    items: [
      { name: 'ad-library', label: 'nav.ad_library', icon: Search, featureKey: 'intel.ad_library' },
    ],
  },
  {
    key: 'resources',
    title: 'navSection.resources',
    featureKey: 'resources',
    items: [
      { name: 'platforms', label: 'nav.platforms', icon: Connection, featureKey: 'resources.platforms' },
      { name: 'commerce', label: 'nav.commerce', icon: Goods, featureKey: 'resources.commerce' },
      { name: 'ad-accounts', label: 'nav.ad_accounts', icon: Wallet, featureKey: 'resources.ad_accounts' },
      { name: 'pages', label: 'nav.pages', icon: Memo, featureKey: 'resources.pages' },
      { name: 'pixels', label: 'nav.pixels', icon: Link, featureKey: 'resources.pixels' },
      { name: 'audiences', label: 'nav.audiences', icon: UserFilled, featureKey: 'resources.audiences' },
    ],
  },
  {
    // No key → always visible (pinned, no header).
    items: [{ name: 'settings', label: 'nav.settings', icon: Setting, featureKey: 'settings' }],
  },
];
